import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  Paper,
  alpha,
  Grid,
  Stack,
  Avatar,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CloudUpload,
  Settings as SettingsIcon,
  Save as SaveIcon,
  Backup as BackupIcon,
  Restore as RestoreIcon,
  Cloud as CloudIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassEmptyIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import backupService, { Backup } from '../services/backupService';
import { Helmet } from 'react-helmet-async';
import Swal from 'sweetalert2';
import { formatarDataHora } from '../utils/formatToView';
import SysKpiCard from '../components/common/SysKpiCard';
import {
  pageShellSx,
  rankingHeaderPaperSx,
  listSectionPaperSx,
  listSectionHeaderBarSx,
  rankingTableHeadRowSx,
  tablePaginationWrapperSx,
} from '../styles/theme';

const BackupManagementPage: React.FC = () => {
  const theme = useTheme();
  const [backups, setBackups] = useState<Backup[]>([]);
  const [loading, setLoading] = useState(true);
  const [backupLoading, setBackupLoading] = useState(false);
  const [uploadingBackup, setUploadingBackup] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadObservacoes, setUploadObservacoes] = useState('');
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Configurações
  const [activeConfigTab, setActiveConfigTab] = useState(0);
  const [savingConfig, setSavingConfig] = useState(false);
  const [testingConnection, setTestingConnection] = useState<string | null>(null);

  // Geral
  const [automaticoEnabled, setAutomaticoEnabled] = useState(false);
  const [retencaoDias, setRetencaoDias] = useState(30);

  // Mega.io
  const [megaEnabled, setMegaEnabled] = useState(false);
  const [megaEmail, setMegaEmail] = useState('');
  const [megaPassword, setMegaPassword] = useState('');

  // FTP
  const [ftpEnabled, setFtpEnabled] = useState(false);
  const [ftpHost, setFtpHost] = useState('');
  const [ftpPort, setFtpPort] = useState(21);
  const [ftpUsername, setFtpUsername] = useState('');
  const [ftpPassword, setFtpPassword] = useState('');
  const [ftpSecure, setFtpSecure] = useState(false);

  useEffect(() => {
    loadBackups();
    loadConfigurations();
  }, []);

  const loadConfigurations = async () => {
    try {
      const configs = await backupService.obterConfiguracoes();

      // Carregar configurações Gerais
      setAutomaticoEnabled(configs.automaticoEnabled || false);
      setRetencaoDias(configs.retencaoDias || 30);

      // Carregar configurações Mega.io
      setMegaEnabled(configs.megaEnabled || false);
      setMegaEmail(configs.megaEmail || '');
      setMegaPassword(configs.megaPassword || '');

      // Carregar configurações FTP
      setFtpEnabled(configs.ftpEnabled || false);
      setFtpHost(configs.ftpHost || '');
      setFtpPort(configs.ftpPort || 21);
      setFtpUsername(configs.ftpUsername || '');
      setFtpPassword(configs.ftpPassword || '');
      setFtpSecure(configs.ftpSecure || false);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      toast.error('Erro ao carregar configurações');
    }
  };

  const handleSaveConfigurations = async () => {
    // Validação Mega.io
    if (megaEnabled && (!megaEmail || !megaPassword)) {
      toast.error('Email e senha são obrigatórios quando Mega.io está habilitado');
      return;
    }

    // Validação FTP
    if (ftpEnabled && (!ftpHost || !ftpUsername || !ftpPassword)) {
      toast.error('Host, usuário e senha são obrigatórios quando FTP está habilitado');
      return;
    }

    try {
      setSavingConfig(true);

      await backupService.salvarConfiguracoes({
        automaticoEnabled,
        retencaoDias,
        megaEnabled,
        megaEmail,
        megaPassword,
        ftpEnabled,
        ftpHost,
        ftpPort,
        ftpUsername,
        ftpPassword,
        ftpSecure,
      });

      toast.success('Configurações salvas com sucesso!');
      setConfigModalOpen(false);
    } catch (error: any) {
      console.error('Erro ao salvar configurações:', error);
      toast.error(error.response?.data?.message || 'Erro ao salvar configurações');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleTestMega = async () => {
    if (!megaEmail || !megaPassword) {
      toast.error('Preencha email e senha para testar');
      return;
    }

    try {
      setTestingConnection('mega');
      const result = await backupService.testMegaConnection(megaEmail, megaPassword);
      if (result.success) {
        toast.success('Conexão com Mega.io bem-sucedida!');
      } else {
        toast.error('Erro ao conectar com Mega.io');
      }
    } catch (error: any) {
      toast.error('Erro ao testar conexão');
    } finally {
      setTestingConnection(null);
    }
  };

  const handleTestFTP = async () => {
    if (!ftpHost || !ftpUsername || !ftpPassword) {
      toast.error('Preencha todos os campos obrigatórios para testar');
      return;
    }

    try {
      setTestingConnection('ftp');
      const result = await backupService.testFTPConnection(ftpHost, ftpPort, ftpUsername, ftpPassword, ftpSecure);
      if (result.success) {
        toast.success('Conexão com FTP bem-sucedida!');
      } else {
        toast.error('Erro ao conectar com FTP');
      }
    } catch (error: any) {
      toast.error('Erro ao testar conexão');
    } finally {
      setTestingConnection(null);
    }
  };


  const loadBackups = async () => {
    try {
      setLoading(true);
      const backupsData = await backupService.listarBackups();
      setBackups(backupsData);
    } catch (error: any) {
      console.error('Erro ao carregar backups:', error);
      toast.error('Erro ao carregar backups');
    } finally {
      setLoading(false);
    }
  };

  const handleNovoBackup = async () => {
    try {
      setBackupLoading(true);
      const result = await backupService.criarBackupManual();
      toast.success('Backup iniciado com sucesso!');
      await loadBackups();
    } catch (error: any) {
      console.error('Erro ao criar backup:', error);
      toast.error('Erro ao criar backup');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleDownload = async (backup: Backup) => {
    try {
      await backupService.downloadBackup(backup.id, backup.nome_arquivo);
      toast.success('Download iniciado!');
    } catch (error: any) {
      console.error('Erro ao fazer download:', error);
      toast.error('Erro ao fazer download do backup');
    }
  };

  const handleDelete = async (backup: Backup) => {
    if (!window.confirm(`Tem certeza que deseja deletar o backup "${backup.nome_arquivo}"?`)) {
      return;
    }

    try {
      await backupService.deletarBackup(backup.id);
      toast.success('Backup deletado com sucesso!');
      await loadBackups();
    } catch (error: any) {
      console.error('Erro ao deletar backup:', error);
      toast.error('Erro ao deletar backup');
    }
  };

  const handleRestore = async (backup: Backup) => {
    const result = await Swal.fire({
      title: '⚠️ Atenção!',
      html: `
        <div style="text-align: left;">
          <p><strong>Esta operação irá RESTAURAR o banco de dados a partir deste backup.</strong></p>
          <p style="margin-top: 15px;">Backup: <strong>${backup.nome_arquivo}</strong></p>
          <p style="margin-top: 15px; color: #d32f2f;">
            ⚠️ Esta é uma operação <strong>IRREVERSÍVEL</strong> que pode sobrescrever dados existentes.
          </p>
          <p style="margin-top: 15px;">Você tem certeza que deseja continuar?</p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, Restaurar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d32f2f',
      cancelButtonColor: '#9e9e9e',
      reverseButtons: true,
      backdrop: true,
      allowOutsideClick: false,
      allowEscapeKey: true,
      focusConfirm: false,
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      // Toast de início
      toast.info('Restauração iniciada... Este processo pode demorar alguns minutos.');

      // Executar restauração
      await backupService.restaurarBackup(backup.id);

      // Toast de sucesso
      toast.success('Backup restaurado com sucesso!');

      // SweetAlert de sucesso
      Swal.fire({
        title: '✅ Restauração Concluída!',
        text: 'O backup foi restaurado com sucesso.',
        icon: 'success',
        confirmButtonText: 'OK',
        confirmButtonColor: '#2e7d32',
      });
    } catch (error: any) {
      console.error('Erro ao restaurar backup:', error);

      // SweetAlert de erro
      Swal.fire({
        title: '❌ Erro ao Restaurar',
        html: `
          <p>Não foi possível restaurar o backup.</p>
          <p style="margin-top: 10px; color: #d32f2f;"><strong>${error.message || 'Erro desconhecido'}</strong></p>
        `,
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d32f2f',
      });
    }
  };

  const handleUploadBackup = async () => {
    if (!selectedFile) {
      toast.error('Selecione um arquivo ZIP');
      return;
    }

    try {
      setUploadingBackup(true);
      await backupService.uploadBackup(selectedFile, uploadObservacoes);
      toast.success('Backup enviado com sucesso!');
      setUploadModalOpen(false);
      setSelectedFile(null);
      setUploadObservacoes('');
      await loadBackups();
    } catch (error: any) {
      console.error('Erro ao enviar backup:', error);
      toast.error('Erro ao enviar backup');
    } finally {
      setUploadingBackup(false);
    }
  };

  const getTipoLabel = (tipo: string) => {
    switch (tipo) {
      case 'manual': return 'Manual';
      case 'automatico': return 'Automático';
      case 'upload': return 'Upload';
      default: return tipo;
    }
  };

  const getTipoColor = (tipo: string): 'primary' | 'success' | 'warning' | 'default' => {
    switch (tipo) {
      case 'manual': return 'primary';
      case 'automatico': return 'success';
      case 'upload': return 'warning';
      default: return 'default';
    }
  };

  const formatarTamanho = (bytes: number | string | null) => {
    const val = bytes == null || bytes === '' ? null : (typeof bytes === 'string' ? parseInt(bytes, 10) : Number(bytes));
    return backupService.formatarTamanho(val);
  };

  const backupsPaginados = Array.isArray(backups)
    ? backups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : [];

  // Estatísticas
  const stats = {
    total: Array.isArray(backups) ? backups.length : 0,
    manual: Array.isArray(backups) ? backups.filter(b => b.tipo === 'manual').length : 0,
    automatico: Array.isArray(backups) ? backups.filter(b => b.tipo === 'automatico').length : 0,
    upload: Array.isArray(backups) ? backups.filter(b => b.tipo === 'upload').length : 0,
    sucesso: Array.isArray(backups) ? backups.filter(b => b.status === 'sucesso').length : 0,
    erro: Array.isArray(backups) ? backups.filter(b => b.status === 'erro').length : 0,
    processando: Array.isArray(backups) ? backups.filter(b => b.status === 'processando').length : 0,
  };

  return (
    <>
      <Helmet>
        <title>Gerenciamento de Backup - SysUp</title>
      </Helmet>

      <Box sx={pageShellSx}>
        <Paper elevation={0} sx={rankingHeaderPaperSx(theme, 'info')}>
          <Box sx={{ position: 'absolute', top: -20, right: -10, opacity: 0.08, transform: 'rotate(15deg)' }}>
            <BackupIcon sx={{ fontSize: 120, color: theme.palette.info.main }} />
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.25}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Avatar sx={{ width: 40, height: 40, background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`, boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.35)}` }}>
                <BackupIcon sx={{ fontSize: 22 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={800} lineHeight={1.2}>Gerenciamento de Backup</Typography>
                <Typography variant="caption" color="text.secondary">Crie, restaure e configure backups do sistema</Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadBackups} disabled={loading}>Atualizar</Button>
              <Button variant="outlined" size="small" startIcon={<CloudUpload />} onClick={() => setUploadModalOpen(true)} disabled={backupLoading}>Enviar</Button>
              <Button variant="outlined" size="small" startIcon={<SettingsIcon />} onClick={async () => { await loadConfigurations(); setConfigModalOpen(true); }}>Config</Button>
              <Button variant="contained" size="small" startIcon={backupLoading ? <CircularProgress size={16} color="inherit" /> : <AddIcon />} onClick={handleNovoBackup} disabled={loading || backupLoading}>
                {backupLoading ? 'Criando...' : 'Novo Backup'}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {!loading && stats.total > 0 && (
          <Grid container spacing={1} mb={1.5}>
            <Grid item xs={6} md={3}><SysKpiCard titulo="Total" valor={stats.total} icone={<BackupIcon sx={{ color: theme.palette.primary.main }} />} cor={theme.palette.primary.main} bgCor={alpha(theme.palette.primary.main, 0.12)} /></Grid>
            <Grid item xs={6} md={3}><SysKpiCard titulo="Sucesso" valor={stats.sucesso} icone={<CheckCircleIcon sx={{ color: theme.palette.success.main }} />} cor={theme.palette.success.main} bgCor={alpha(theme.palette.success.main, 0.12)} /></Grid>
            <Grid item xs={6} md={3}><SysKpiCard titulo="Erros" valor={stats.erro} icone={<ErrorIcon sx={{ color: theme.palette.error.main }} />} cor={theme.palette.error.main} bgCor={alpha(theme.palette.error.main, 0.12)} /></Grid>
            <Grid item xs={6} md={3}><SysKpiCard titulo="Processando" valor={stats.processando} icone={<HourglassEmptyIcon sx={{ color: theme.palette.warning.main }} />} cor={theme.palette.warning.main} bgCor={alpha(theme.palette.warning.main, 0.12)} /></Grid>
          </Grid>
        )}

        <Paper elevation={0} sx={listSectionPaperSx(theme)}>
          <Box sx={listSectionHeaderBarSx(theme)}>
            <Typography variant="body2" fontWeight={700}>Listagem de Backups</Typography>
            <Chip size="small" label={`${stats.total} backup${stats.total !== 1 ? 's' : ''}`} color="primary" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
          </Box>

          {loading ? (
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={32} />
              <Typography variant="caption" color="text.secondary">Carregando backups...</Typography>
            </Box>
          ) : backupsPaginados.length === 0 ? (
            <Box sx={{ p: 2.5, textAlign: 'center' }}>
              <BackupIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.25), mb: 1 }} />
              <Typography variant="subtitle2" fontWeight={600}>Nenhum backup encontrado</Typography>
              <Typography variant="caption" color="text.secondary">Clique em "Novo Backup" para criar o primeiro</Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={rankingTableHeadRowSx(theme)}>
                      <TableCell>ID</TableCell>
                      <TableCell>Data/Hora</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Tamanho</TableCell>
                      <TableCell align="right">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {backupsPaginados.map((backup) => (
                      <TableRow key={backup.id} hover sx={{ '& .MuiTableCell-root': { py: 0.6, px: 1 }, '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>#{backup.id}</TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{formatarDataHora(backup.data_criacao)}</TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label={getTipoLabel(backup.tipo)} color={getTipoColor(backup.tipo)} size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label={backup.status} color={backupService.obterCorStatus(backup.status)} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{formatarTamanho(backup.tamanho_bytes)}</TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }} align="right">
                          <Box display="flex" gap={0.5} justifyContent="flex-end">
                            {backup.status === 'sucesso' && (
                              <>
                                <Tooltip title="Restaurar">
                                  <IconButton size="small" color="warning" onClick={() => handleRestore(backup)}>
                                    <RestoreIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Download">
                                  <IconButton size="small" color="primary" onClick={() => handleDownload(backup)}>
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            <Tooltip title="Deletar">
                              <IconButton size="small" color="error" onClick={() => handleDelete(backup)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {!loading && Array.isArray(backups) && backups.length > 0 && (
                <>
                  <Divider />
                  <Box sx={tablePaginationWrapperSx(theme)}>
                    <TablePagination
                      component="div"
                      size="small"
                      count={backups.length}
                      page={page}
                      onPageChange={(_, p) => setPage(p)}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      labelRowsPerPage="Linhas:"
                      sx={{ '.MuiTablePagination-toolbar': { minHeight: 44, px: 1.5 }, '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '0.75rem', my: 0 } }}
                    />
                  </Box>
                </>
              )}
            </>
          )}
        </Paper>

        {/* Modal de Configurações */}
        <Dialog
          open={configModalOpen}
          onClose={() => setConfigModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <SettingsIcon />
              Configurações de Backup
            </Box>
          </DialogTitle>

          <DialogContent sx={{ pt: 1 }}>
            <Alert severity="info" sx={{ mb: 2, mt: 0 }}>
              As configurações de nuvem são utilizadas apenas pelo backup automático (cron às 3h da manhã).
            </Alert>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={activeConfigTab} onChange={(e, newValue) => setActiveConfigTab(newValue)}>
                <Tab label="⚙️ Geral" />
                <Tab label="📦 Mega.io" />
                <Tab label="📁 FTP" />
              </Tabs>
            </Box>

            {/* Conteúdo da Aba Geral */}
            {activeConfigTab === 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Configurações Gerais
                </Typography>

                <Box sx={{ mb: 3, mt: 2, p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={automaticoEnabled}
                        onChange={(e) => setAutomaticoEnabled(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Backup Automático Diário
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Executa automaticamente todos os dias às 03:00 e 20:00.
                        </Typography>
                      </Box>
                    }
                  />
                </Box>

                <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Retenção de Backups Locais
                  </Typography>
                  <TextField
                    fullWidth
                    label="Dias de Retenção"
                    type="number"
                    value={retencaoDias}
                    onChange={(e) => setRetencaoDias(parseInt(e.target.value) || 0)}
                    helperText="Número de dias para manter os arquivos de backup no servidor (0 = nunca excluir)."
                    size="small"
                  />
                </Box>
              </Box>
            )}

            {/* Conteúdo da Aba Mega.io */}
            {activeConfigTab === 1 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Configuração Mega.io
                </Typography>

                {/* Switch para habilitar/desabilitar */}
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant={megaEnabled ? 'contained' : 'outlined'}
                    color={megaEnabled ? 'success' : undefined}
                    onClick={() => setMegaEnabled(!megaEnabled)}
                    startIcon={megaEnabled ? <CheckCircleIcon /> : null}
                  >
                    {megaEnabled ? 'Mega.io Habilitado' : 'Habilitar Mega.io'}
                  </Button>
                </Box>

                {megaEnabled && (
                  <>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={megaEmail}
                      onChange={(e) => setMegaEmail(e.target.value)}
                      sx={{ mb: 1.5 }}
                      helperText="Email da conta Mega.io"
                    />
                    <TextField
                      fullWidth
                      label="Senha"
                      type="password"
                      value={megaPassword}
                      onChange={(e) => setMegaPassword(e.target.value)}
                      sx={{ mb: 1.5 }}
                      helperText="Senha da conta Mega.io"
                    />

                    <Box display="flex" gap={2} mt={2}>
                      <Button
                        variant="outlined"
                        color="info"
                        onClick={handleTestMega}
                        disabled={testingConnection === 'mega' || !megaEmail || !megaPassword}
                        startIcon={testingConnection === 'mega' ? <CircularProgress size={20} /> : <CloudIcon />}
                      >
                        {testingConnection === 'mega' ? 'Testando...' : 'Testar Conexão'}
                      </Button>
                    </Box>

                    {testingConnection === 'mega' && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Testando conexão com Mega.io...
                      </Alert>
                    )}
                  </>
                )}
              </Box>
            )}

            {/* Conteúdo da Aba FTP */}
            {activeConfigTab === 2 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Configuração Servidor FTP
                </Typography>

                {/* Switch para habilitar/desabilitar */}
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant={ftpEnabled ? 'contained' : 'outlined'}
                    color={ftpEnabled ? 'success' : undefined}
                    onClick={() => setFtpEnabled(!ftpEnabled)}
                    startIcon={ftpEnabled ? <CheckCircleIcon /> : null}
                  >
                    {ftpEnabled ? 'FTP Habilitado' : 'Habilitar FTP'}
                  </Button>
                </Box>

                {ftpEnabled && (
                  <>
                    <TextField
                      fullWidth
                      label="Host"
                      value={ftpHost}
                      onChange={(e) => setFtpHost(e.target.value)}
                      sx={{ mb: 1.5 }}
                      helperText="Endereço do servidor FTP (ex: ftp.exemplo.com)"
                    />

                    <TextField
                      fullWidth
                      label="Porta"
                      type="number"
                      value={ftpPort}
                      onChange={(e) => setFtpPort(parseInt(e.target.value) || 21)}
                      sx={{ mb: 1.5 }}
                      helperText="Porta do servidor FTP (padrão: 21 para FTP, 990 para FTPS)"
                    />

                    <TextField
                      fullWidth
                      label="Usuário"
                      value={ftpUsername}
                      onChange={(e) => setFtpUsername(e.target.value)}
                      sx={{ mb: 1.5 }}
                      helperText="Usuário do servidor FTP"
                    />

                    <TextField
                      fullWidth
                      label="Senha"
                      type="password"
                      value={ftpPassword}
                      onChange={(e) => setFtpPassword(e.target.value)}
                      sx={{ mb: 1.5 }}
                      helperText="Senha do servidor FTP"
                    />

                    <Box sx={{ mb: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={ftpSecure}
                            onChange={(e) => setFtpSecure(e.target.checked)}
                          />
                        }
                        label="Usar FTPS (FTP Seguro)"
                      />
                    </Box>

                    <Box display="flex" gap={2} mt={2}>
                      <Button
                        variant="outlined"
                        color="info"
                        onClick={handleTestFTP}
                        disabled={testingConnection === 'ftp' || !ftpHost || !ftpUsername || !ftpPassword}
                        startIcon={testingConnection === 'ftp' ? <CircularProgress size={20} /> : <CloudIcon />}
                      >
                        {testingConnection === 'ftp' ? 'Testando...' : 'Testar Conexão'}
                      </Button>
                    </Box>

                    {testingConnection === 'ftp' && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Testando conexão com servidor FTP...
                      </Alert>
                    )}
                  </>
                )}
              </Box>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setConfigModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveConfigurations}
              disabled={savingConfig}
              startIcon={savingConfig ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {savingConfig ? 'Salvando...' : 'Salvar Configurações'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal de Upload */}
        <Dialog
          open={uploadModalOpen}
          onClose={() => {
            setUploadModalOpen(false);
            setSelectedFile(null);
            setUploadObservacoes('');
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Enviar Backup Existente</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Selecione um arquivo de backup (.zip) para enviar ao servidor.
            </Alert>

            <input
              type="file"
              accept=".zip"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setSelectedFile(file);
                }
              }}
              style={{ display: 'none' }}
              id="backup-upload-file"
            />
            <label htmlFor="backup-upload-file">
              <Button
                variant="outlined"
                component="span"
                fullWidth
                startIcon={<CloudUpload />}
                sx={{ mb: 2 }}
              >
                {selectedFile ? selectedFile.name : 'Selecionar Arquivo ZIP'}
              </Button>
            </label>

            <TextField
              fullWidth
              label="Observações (opcional)"
              multiline
              rows={2}
              value={uploadObservacoes}
              onChange={(e) => setUploadObservacoes(e.target.value)}
              placeholder="Observações sobre este backup..."
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setUploadModalOpen(false);
                setSelectedFile(null);
                setUploadObservacoes('');
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              startIcon={uploadingBackup ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleUploadBackup}
              disabled={uploadingBackup || !selectedFile}
            >
              {uploadingBackup ? 'Enviando...' : 'Enviar'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default BackupManagementPage;

